import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import ModalWrapper from "../../../../../../Components/CustomModal/ModalWrapper";
import { Dropdown } from "primereact/dropdown";
import axios from "axios";
import { V_URL } from "../../../../../../BaseUrl";
import { getViewIssReturnItems } from "../../../../../../Store/prosafe/proTransaction/ProIssReturn/ViewIssReturnItems";
import { getAvailableLot } from "../../../../../../Store/Store/Transformation/issue/getAvailableLot";
import { getAvailablePiece } from "../../../../../../Store/Store/Transformation/issue/getAvailablePiece";
import { MultiSelect } from "primereact/multiselect";
import { CONSUMABLE_LIST } from "../../../../../../config/DataFile";

const buildInitialFormState = () => ({
  item_id: "",
  item_no: "",
  item_length: "",
  item_width: "",
  item_weight: "",
  item_radius: "",
  item_thickness: "",
  unit: "",
  category: "",
  assembly_weight: "",
  assembly_surface_area: "",
  rate: "",
  amount: "",
  gst: "",
  gst_amount: "",
  total_amount: "",
  heat_no: "",
  plat_no: "",
  tc_no: "",
  remarks: "",
  quantity: "",
  isAuto: false,
  from_id: null,
  detail_id: null,
  issue_type: "CONSUMABLE",
  lots: [],
  piece_ids: [],
});

// Helper to compute lot_issue_qty based on stock_uom_type and dimensions
const computeLotIssueQty = (lot) => {
  const uom = lot?.item_detail_data?.stock_uom_type;
  const dims = lot.dimensions || {};
  const length = Number(dims.lot_length) || 0;
  const width = Number(dims.lot_width) || 0;
  const weight = Number(dims.lot_weight) || 0;
  switch (uom) {
    case "LENGTH":
      return length;
    case "AREA":
      return length * width;
    case "WEIGHT":
      return weight;
    case "QTY":
    default:
      // For QTY, use length as quantity (or fallback to 0)
      return length;
  }
};

const buildLotEntry = (lot) => {
  const base = {
    lot_id: lot._id,
    dimensions: {
      lot_length: lot.dimensions?.length ?? 0,
      lot_width: lot.dimensions?.width ?? 0,
      lot_weight: lot.dimensions?.weight ?? 0,
    },
    lot_no: lot.lot_no,
    issued_qty: lot.issued_qty,
    item_detail_data: {
      stock_uom_type: lot?.item_detail_data?.stock_uom_type,
      transform_type: lot?.item_detail_data?.transform_type
    },
  };
  // MODIFIED: compute initial lot_issue_qty
  base.lot_issue_qty = computeLotIssueQty(base);
  return base;
};

const sanitizeLots = (lots) =>
  lots.map(({ lot_id, dimensions, lot_issue_qty }) => ({
    lot_id,
    dimensions,
    lot_issue_qty: Number(lot_issue_qty) || 0,
  }));

const ITEM_FIELDS = [
  { name: "item_no", label: "Item No", type: "text" },
  { name: "item_length", label: "Length", type: "number" },
  { name: "item_width", label: "Width", type: "number" },
  { name: "item_weight", label: "Weight", type: "number" },
  { name: "item_radius", label: "Radius", type: "number" },
  { name: "item_thickness", label: "Thickness", type: "text" },
  { name: "assembly_weight", label: "Assembly Weight", type: "number" },
  { name: "assembly_surface_area", label: "Surface Area", type: "number" },
  { name: "rate", label: "Rate (₹)", type: "number", required: true },
  { name: "amount", label: "Amount (₹)", type: "number", disabled: true },
  { name: "gst", label: "GST (%)", type: "number" },
  { name: "gst_amount", label: "GST Amount (₹)", type: "number", disabled: true },
  { name: "total_amount", label: "Total Amount (₹)", type: "number", disabled: true },
  { name: "heat_no", label: "Heat No.", type: "text", required: true },
  { name: "plat_no", label: "Plat No.", type: "text" },
  { name: "tc_no", label: "TC No.", type: "text" },
  { name: "remarks", label: "Remarks", type: "text" },
];

const ManualIssReturnModel = ({
  modal,
  itemsArr,
  editingIndex,
  setEditingIndex,
  onSubmit,
  data,
  editItemId,
  setEditItemId,
}) => {
  const dispatch = useDispatch();
  const itemData = useSelector((s) => s.getItem?.user?.data) ?? [];
  const lotData = useSelector((s) => s.getAvailableLot?.user?.data) ?? [];
  const pieceData = useSelector((s) => s.getAvailablePiece?.user?.data) ?? [];

  const [formData, setFormData] = useState(buildInitialFormState);
  const [errors, setErrors] = useState({});
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (editingIndex !== null && itemsArr[editingIndex]) {
      const row = itemsArr[editingIndex];
      const enrichedLots = (row.lots ?? []).map((l) => ({
        ...l,
        lot_no: l.lot_no ?? "",
        issued_qty: l.issued_qty ?? 0,
        item_detail_data: l.item_detail_data,
        // MODIFIED: ensure lot_issue_qty is computed (if not present, compute now)
        lot_issue_qty: l.lot_issue_qty ?? computeLotIssueQty(l),
      }));

      const normalizedPieceIds = (row.piece_ids ?? []).map((p) =>
        typeof p === "object" ? p._id : p
      );

      setFormData({ ...buildInitialFormState(), ...row, lots: enrichedLots, piece_ids: normalizedPieceIds, });
    } else {
      setFormData(buildInitialFormState());
    }
  }, [editingIndex, itemsArr]);

  useEffect(() => {
    if (!modal.isShown) {
      setFormData(buildInitialFormState());
      setEditingIndex(null);
      setEditItemId(null);
      setErrors({});
    }
  }, [modal.isShown]);

  useEffect(() => {
    if (!formData.item_id) return;

    if (formData.issue_type === "LOT") {
      dispatch(
        getAvailableLot({
          item_id: formData.item_id,
          status: ["PARTIALLY_USED", "FULLY_USED"],
        })
      );
    }

    if (formData.issue_type === "PIECE") {
      dispatch(
        getAvailablePiece({
          item_id: formData.item_id,
          status: "ISSUED",
        })
      );
    }
  }, [dispatch, formData.item_id, formData.issue_type, formData.lots]);

  useEffect(() => {
    if (!formData.item_id) return;
    const selected = itemData.find((i) => i._id === formData.item_id);
    if (!selected) return;
    setFormData((prev) => ({
      ...prev,
      unit: selected?.unit?.name ?? "",
      category: selected?.category?.name ?? "",
      gst: editingIndex === null ? (selected?.gst_percentage ?? "") : prev.gst,
    }));
  }, [formData.item_id, itemData]);

  // MODIFIED: quantity for LOT type now sums lot_issue_qty instead of counting lots
  useEffect(() => {
    if (formData.issue_type === "LOT") {
      const { totalQty, lotCount } = (formData.lots ?? []).reduce(
        (acc, lot) => {
          acc.totalQty += Number(lot?.lot_issue_qty) || 0;
          acc.lotCount += 1;
          return acc;
        },
        { totalQty: 0, lotCount: 0 }
      );
      setFormData((prev) => ({ ...prev, quantity: lotCount }));
    } else if (formData.issue_type === "PIECE") {
      setFormData((prev) => ({ ...prev, quantity: 0 }));
    }
    // CONSUMABLE → user enters quantity manually, no override
  }, [formData.issue_type, formData.lots]);

  useEffect(() => {
    const qty = parseFloat(formData.quantity) || 0;
    const rate = parseFloat(formData.rate) || 0;
    const gst = parseFloat(formData.gst) || 0;
    const amount = qty * rate;
    const gst_amount = (amount * gst) / 100;
    setFormData((prev) => ({
      ...prev,
      amount: amount.toFixed(2),
      gst_amount: gst_amount.toFixed(2),
      total_amount: (amount + gst_amount).toFixed(2),
    }));
  }, [formData.quantity, formData.rate, formData.gst]);

  const otherRows = itemsArr?.filter(
    (item, idx) => item.item_id === formData.item_id && idx !== editingIndex
  );

  const usedLotIds = otherRows?.flatMap((r) => r.lots?.map((l) => l.lot_id) ?? []) ?? [];
  const usedPieceIds = otherRows?.flatMap((r) => r.piece_ids ?? []) ?? [];

  const currentLotIds = formData.lots?.map((l) => l.lot_id) ?? [];

  const lotOptions = lotData
    .filter((lot) => !usedLotIds.includes(lot._id) || currentLotIds.includes(lot._id))
    .map((lot) => ({
      label: `${lot.lot_no} ISSUE QTY: ${lot.issued_qty}`,
      value: lot._id,
      _raw: lot,
    }));

  const currentPieceIds = formData.piece_ids ?? [];

  const pieceOptions = pieceData
    .filter((p) => !usedPieceIds.includes(p._id) || currentPieceIds.includes(p._id))
    .map((p) => ({ label: p.piece_no, value: p._id }));

  const getAvailableQty = (lotId) => {
    const findLotData = lotData.find((e) => e?._id === lotId);
    return findLotData?.issued_qty ?? 0;
  };

  const clearError = (name) =>
    setErrors((prev) => { const e = { ...prev }; delete e[name]; return e; });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleLotMultiSelectChange = useCallback(
    (selectedIds) => {
      const existingMap = new Map(
        (formData.lots ?? []).map((l) => [l.lot_id, l])
      );

      const merged = selectedIds.map((lotId) => {
        if (existingMap.has(lotId)) return existingMap.get(lotId); // keep edits
        const rawLot = lotData.find((l) => l._id === lotId);
        return rawLot ? buildLotEntry(rawLot) : { lot_id: lotId, dimensions: {}, lot_issue_qty: 0, lot_no: "", issued_qty: 0, item_detail_data: {} };
      });

      setFormData((prev) => ({ ...prev, lots: merged }));
      clearError("lots");
    },
    [formData.lots, lotData]
  );

  // MODIFIED: recompute lot_issue_qty whenever dimensions change
  const handleLotFieldChange = (lotId, field, rawValue) => {
    const value = rawValue === "" ? "" : Number(rawValue);
    setFormData((prev) => {
      const updatedLots = prev.lots.map((l) => {
        if (l.lot_id !== lotId) return l;
        let updatedLot;
        if (field === "lot_issue_qty") {
          // Not used directly (field is read-only), but keep for completeness
          updatedLot = { ...l, lot_issue_qty: value };
        } else {
          const dimKey = field; // "lot_length", "lot_width", "lot_weight"
          const newDims = { ...l.dimensions, [dimKey]: value };
          updatedLot = { ...l, dimensions: newDims };
        }
        // Recompute issue quantity based on updated dimensions and UOM
        const computedQty = computeLotIssueQty(updatedLot);
        return { ...updatedLot, lot_issue_qty: computedQty };
      });
      return { ...prev, lots: updatedLots };
    });
  };

  const validateForm = () => {
    const e = {};
    if (!formData.item_id) e.item_id = "Item is required";
    if (formData.issue_type === "CONSUMABLE") {
      if (!formData.quantity || parseFloat(formData.quantity) <= 0)
        e.quantity = "Valid quantity is required";
    }
    if (!formData.rate || parseFloat(formData.rate) <= 0)
      e.rate = "Valid rate is required";
    if (!formData.heat_no) e.heat_no = "Heat No. is required";

    if (formData.issue_type === "LOT") {
      if (!formData.lots?.length) {
        e.lots = "Select at least one lot";
      } else {
        // MODIFIED: check each lot's issue qty does not exceed available
        const invalidLots = formData.lots.filter((lot) => {
          const availableQty = getAvailableQty(lot.lot_id);
          const issueQty = Number(lot.lot_issue_qty) || 0;

          return issueQty > availableQty;
        });
        if (invalidLots.length > 0) {
          e.lots = "Some lots exceed available quantity";
        }
      }
    }

    if (formData.issue_type === "PIECE" && !formData.piece_ids?.length) {
      e.piece_ids = "Select at least one piece";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildItemsDetailPayload = () => ({
    item_no: formData.item_no,
    item_id: formData.item_id,
    item_length: Number(formData.item_length) || 0,
    item_width: Number(formData.item_width) || 0,
    item_weight: Number(formData.item_weight) || 0,
    item_radius: Number(formData.item_radius) || 0,
    item_thickness: formData.item_thickness || null,
    assembly_weight: Number(formData.assembly_weight) || 0,
    assembly_surface_area: Number(formData.assembly_surface_area) || 0,
    quantity: Number(formData.quantity),
    rate: Number(formData.rate),
    amount: Number(formData.amount),
    gst: Number(formData.gst) || 0,
    gst_amount: Number(formData.gst_amount) || 0,
    total_amount: Number(formData.total_amount) || 0,
    heat_no: formData.heat_no || null,
    plat_no: formData.plat_no || null,
    tc_no: formData.tc_no || null,
    remarks: formData.remarks || null,
    issue_type: formData.issue_type,
    lots: formData.issue_type === "LOT" ? sanitizeLots(formData.lots) : [],
    piece_ids: formData.issue_type === "PIECE" ? (formData.piece_ids ?? []) : [],
  });

  const handleFormAction = async (e, isAddMore = false) => {
    e.preventDefault();
    if (!validateForm()) return;
    setDisable(true);

    try {
      if (editItemId) {
        const payload = {
          id: data._id,
          item_detail_id: editItemId,
          items_details: buildItemsDetailPayload(),
        };
        const resp = await axios.put(
          `${V_URL}/prosafe/pro-update-isr-item`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem("PAY_USER_TOKEN")}` } }
        );
        if (resp.data.success) {
          toast.success(resp.data.message);
          dispatch(getViewIssReturnItems({ formData: { id: data._id } }));
          if (isAddMore) {
            setFormData(buildInitialFormState());
            setEditingIndex(null);
            setEditItemId(null);
            setErrors({});
          } else {
            modal.hide();
            setEditingIndex(null);
            setEditItemId(null);
          }
        } else {
          toast.error(resp.data.message);
        }
      } else if (data?._id) {
        const payload = {
          id: data._id,
          items_details: JSON.stringify([
            { ...buildItemsDetailPayload(), item_id: formData.item_id },
          ]),
        };
        const resp = await axios.post(
          `${V_URL}/prosafe/pro-add-isr-item`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem("PAY_USER_TOKEN")}` } }
        );
        if (resp.data.success) {
          toast.success(resp.data.message);
          dispatch(getViewIssReturnItems({ formData: { id: data._id } }));
          if (isAddMore) {
            setFormData(buildInitialFormState());
            setEditingIndex(null);
            setEditItemId(null);
            setErrors({});
          } else {
            modal.hide();
          }
        } else {
          toast.error(resp.data.message);
        }
      } else {
        onSubmit({
          ...buildItemsDetailPayload(),
          item_id: formData.item_id,
          isAuto: formData.isAuto,
          from_id: formData.from_id,
          detail_id: formData.detail_id,
        });
        if (isAddMore) {
          setFormData(buildInitialFormState());
          setEditingIndex(null);
          setEditItemId(null);
          setErrors({});
        } else {
          modal.hide();
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setDisable(false);
    }
  };

  const selectedLotIds = formData.lots?.map((l) => l.lot_id) ?? [];

  return (
    <ModalWrapper
      title="Manual Items Details"
      isShown={modal.isShown}
      hide={modal.hide}
      footer={false}
      customSize="modal-90w"
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="row">
          <div className="col-lg-4 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Item Name <span className="login-danger">*</span></label>
              <Dropdown
                placeholder="Select Item"
                filter
                filterBy="label"
                enforceFocus={false}
                appendTo="self"
                value={formData.item_id}
                options={itemData.map((e) => ({ label: e?.name, value: e?._id }))}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...buildInitialFormState(),
                    item_id: e.value,
                    issue_type: prev.issue_type,
                  }));
                  clearError("item_id");
                }}
                className={`w-100 ${errors.item_id ? "p-invalid" : ""}`}
                disabled={!!editItemId || formData.isAuto}
              />
              {errors.item_id && <small className="p-error">{errors.item_id}</small>}
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Issue Type</label>
              <Dropdown
                value={formData.issue_type}
                options={CONSUMABLE_LIST.map((t) => ({ label: t.label, value: t.value }))}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    issue_type: e.value,
                    lots: [],
                    piece_ids: [],
                  }));
                  clearError("lots");
                  clearError("piece_ids");
                }}
                placeholder="Select Issue Type"
                filter
                appendTo="self"
                className="w-100"
                disabled={!formData.item_id}
              />
            </div>
          </div>

          {formData.item_id && (
            <>
              <div className="col-lg-4 col-md-6 col-12">
                <div className="input-block local-forms">
                  <label>Unit</label>
                  <input className="form-control" value={formData.unit || "—"} disabled />
                </div>
              </div>
              <div className="col-lg-4 col-md-6 col-12">
                <div className="input-block local-forms">
                  <label>Category</label>
                  <input className="form-control" value={formData.category || "—"} disabled />
                </div>
              </div>
            </>
          )}

          {formData.issue_type === "LOT" && (
            <div className="col-12">
              <div className="input-block local-forms">
                <label>
                  Lot No(s) <span className="text-danger">*</span>
                </label>
                <MultiSelect
                  value={selectedLotIds}
                  options={lotOptions}
                  onChange={(e) => handleLotMultiSelectChange(e.value)}
                  placeholder="Select Lot(s)"
                  filter
                  className={`w-100 ${errors.lots ? "p-invalid" : ""}`}
                  disabled={!formData.item_id}
                />
                {errors.lots && <small className="p-error">{errors.lots}</small>}
              </div>

              {formData.lots?.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-bordered table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Lot No</th>
                        <th>Length</th>
                        <th>Width</th>
                        <th>Weight</th>
                        <th>Available</th>
                        {/* MODIFIED: added Issue Qty column */}
                        <th>Issue Qty</th>
                        <th>Type</th>
                        <th>UOM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.lots.map((lot, idx) => {
                        // MODIFIED: determine if issue qty exceeds available
                        const availableQty = getAvailableQty(lot.lot_id);
                        const issueQty = lot.lot_issue_qty || 0;
                        const exceeds = issueQty > (availableQty || 0);
                        return (
                          <tr key={lot.lot_id}>
                            <td>{idx + 1}</td>
                            <td>
                              <strong>{lot?.lot_no}</strong>
                            </td>

                            {(["lot_length", "lot_width", "lot_weight"]).map((dim) => (
                              <td key={dim} style={{ minWidth: 90 }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  className="form-control form-control-sm"
                                  value={lot.dimensions?.[dim] ?? 0}
                                  onChange={(e) =>
                                    handleLotFieldChange(lot.lot_id, dim, e.target.value)
                                  }
                                />
                              </td>
                            ))}
                            <td>{availableQty}</td>
                            {/* MODIFIED: read-only issue qty with validation warning */}
                            <td>
                              <span className={exceeds ? "text-danger fw-bold" : ""}>
                                {issueQty.toFixed(2)}
                              </span>
                              {exceeds && (
                                <small className="text-danger d-block">Exceeds available</small>
                              )}
                            </td>
                            <td>{lot?.item_detail_data?.transform_type}</td>
                            <td>{lot?.item_detail_data?.stock_uom_type || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {formData.issue_type === "PIECE" && (
            <div className="col-12">
              <div className="input-block local-forms">
                <label>
                  Piece No(s) <span className="text-danger">*</span>
                </label>
                <MultiSelect
                  value={formData.piece_ids}
                  options={pieceOptions}
                  onChange={(e) => handleDropdownChange("piece_ids", e.value)}
                  placeholder="Select Piece(s)"
                  filter
                  className={`w-100 ${errors.piece_ids ? "p-invalid" : ""}`}
                  disabled={!formData.item_id}
                />
                {errors.piece_ids && <small className="p-error">{errors.piece_ids}</small>}
              </div>
            </div>
          )}

          <div className="col-lg-4 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>
                Return Qty <span className="login-danger">*</span>
                {formData.issue_type === "LOT" && (
                  <span className="ms-1 text-muted small">(sum of lot issue qty)</span>
                )}
                {formData.issue_type === "PIECE" && (
                  <span className="ms-1 text-muted small">(fixed at 0 for piece type)</span>
                )}
              </label>
              <input
                type="number"
                className={`form-control ${errors.quantity ? "is-invalid" : ""}`}
                name="quantity"
                value={formData.quantity ?? ""}
                onChange={handleChange}
                disabled={formData.issue_type === "LOT" || formData.issue_type === "PIECE"}
                placeholder={formData.issue_type === "PIECE" ? "0" : "Enter qty"}
              />
              {errors.quantity && (
                <div className="invalid-feedback">{errors.quantity}</div>
              )}
            </div>
          </div>

          {ITEM_FIELDS.map(({ name, label, required, type, disabled }) => (
            <div key={name} className="col-lg-4 col-md-6 col-12">
              <div className="input-block local-forms">
                <label>
                  {label}{" "}
                  {required && <span className="login-danger">*</span>}
                </label>
                <input
                  type={type ?? "text"}
                  className={`form-control ${errors[name] ? "is-invalid" : ""}`}
                  name={name}
                  value={formData[name] ?? ""}
                  onChange={handleChange}
                  disabled={disabled ?? false}
                />
                {errors[name] && (
                  <div className="invalid-feedback">{errors[name]}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="col-12 text-end mt-2">
          <button
            type="button"
            className="btn btn-primary"
            disabled={disable}
            onClick={(e) => handleFormAction(e, false)}
          >
            {editingIndex !== null ? "Update" : "Submit"}
          </button>

          {editingIndex === null && (
            <button
              type="button"
              className="btn btn-outline-primary ms-2"
              disabled={disable}
              onClick={(e) => handleFormAction(e, true)}
            >
              Add More
            </button>
          )}
        </div>
      </form>
    </ModalWrapper>
  );
};

export default ManualIssReturnModel;